import React, { ChangeEvent, memo, ReactNode } from "react"
import { InputAdornment, TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from "@mui/material"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"

type PropsFromMui =
  | "label"
  | "value"
  | "helperText"
  | "placeholder"
  | "size"
  | "fullWidth"
  | "error"
  | "disabled"
  | "autoComplete"
  | "inputRef"
  | "InputProps"
  | "defaultValue"
  | "onFocus"
  | "onBlur"
  | "className"
  | "InputLabelProps"
  | "multiline"
  | "minRows"

export type TextFieldProps = Pick<MuiTextFieldProps, PropsFromMui> & {
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  dark?: boolean
  disableAutocomplete?: boolean
  required?: boolean
  maxLength?: number
}

export const TextField = memo((props: TextFieldProps) => {
  const {
    startAdornment,
    endAdornment,
    onChange,
    disabled,
    error: _,
    dark = false,
    disableAutocomplete,
    label,
    required,
    maxLength,
    autoComplete,
    size,
    InputProps,
    ...restProps
  } = props
  let { error } = props

  // We do not handle disabled + errored state. Only apply disabled if both
  if (disabled && error) {
    error = false
  }

  return (
    <OutlinedMuiTextField
      $dark={dark}
      InputProps={{
        ...InputProps,
        startAdornment: !!startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
        endAdornment: !!endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
      }}
      inputProps={maxLength ? { maxLength } : undefined}
      onChange={onChange}
      error={error}
      disabled={disabled}
      autoComplete={!!disableAutocomplete ? "new-password" : autoComplete}
      size={size}
      label={`${label}${required ? "*" : ""}`}
      {...restProps}
    />
  )
})

const baseCss = css`
  & .MuiOutlinedInput-root {
    border-radius: ${theme.borderRadius};
    &.MuiInputBase-multiline {
      display: flex;
      padding: ${theme.spacing.m} ${theme.spacing.l};
      .MuiInputAdornment-positionEnd {
        height: auto;
        max-height: unset;
        align-self: flex-end;
        margin-right: -${theme.spacing.m};
      }
    }
  }
`

const normalCssEnabled = baseCss

const normalCssDisabled = css`
  ${baseCss}

  & .MuiOutlinedInput-root {
    background-color: ${theme.color.white2};
  }
`

const darkCss = css`
  // When the input is focused
  & .MuiInputBase-root:focus .MuiOutlinedInput-notchedOutline,
  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.white};
  }

  & .MuiOutlinedInput-notchedOutline {
    border: solid 1px ${theme.color.white};
  }

  & .MuiTypography-colorTextSecondary {
    color: ${theme.color.grey2};
  }
`

const darkCssEnabled = css`
  ${baseCss}
  ${darkCss}

  // When the input is clicked or hovered
  & .MuiFormLabel-root,
  .MuiInputBase-input,
  .MuiInputBase-root:active .MuiOutlinedInput-notchedOutline,
  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    color: ${theme.color.white};
    border-color: ${theme.color.white};
  }

  // The text below the text field
  & .MuiFormHelperText-root {
    color: ${theme.color.grey2};
  }
`

const darkCssDisabled = css`
  ${baseCss}
  ${darkCss}

  & .MuiOutlinedInput-root {
    background-color: ${theme.color.black5};
  }

  // When the input is clicked or hovered
  & .MuiFormLabel-root,
  .MuiInputBase-input,
  .MuiInputBase-root:active .MuiOutlinedInput-notchedOutline,
  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    color: ${theme.color.black4};
  }

  // The text below the text field
  & .MuiFormHelperText-root {
    color: ${theme.color.grey2};
  }
`

const darkCssErrored = css`
  ${baseCss}

  // When the input is clicked or hovered
  & .MuiInputBase-root:focus .MuiOutlinedInput-notchedOutline,
  .Mui-focused .MuiOutlinedInput-notchedOutline,
  .MuiFormLabel-root,
  .MuiInputBase-input,
  .MuiInputBase-root:active .MuiOutlinedInput-notchedOutline,
  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    color: ${theme.color.white};
    border-color: ${theme.color.red2};
  }

  // The text below the text field
  & .MuiFormHelperText-root {
    color: ${theme.color.white};
  }

  & .MuiFormLabel-root {
    color: ${theme.color.red2};
  }

  & .MuiTypography-colorTextSecondary {
    color: ${theme.color.grey2};
  }
`

const getCss = (dark: boolean, disabled: boolean, errored: boolean) => {
  if (!dark && !disabled) return normalCssEnabled
  if (!dark && disabled) return normalCssDisabled
  if (dark && errored) return darkCssErrored
  if (dark && !disabled) return darkCssEnabled
  if (dark && disabled) return darkCssDisabled

  return normalCssEnabled
}

const smallCss = css`
  & .MuiOutlinedInput-root {
    min-height: ${theme.height.xl};
  }
`

const mediumCss = css`
  & .MuiOutlinedInput-root {
    min-height: ${theme.height.lx};
  }
`

const getCssForSize = (size: "small" | "medium" | undefined) => {
  if (size === "small") {
    return smallCss
  }

  return mediumCss
}

const getCssForHelperText = (withHelperText: boolean) => {
  return withHelperText
    ? css`
        p {
          margin: 3px 0 0 ${theme.spacing.l};
          line-height: ${theme.lineHeight.s};
          font-size: ${theme.fontSize.s};
          color: ${theme.color.grey2};
        }
        padding-bottom: 22px;
      `
    : css`
        padding-bottom: 35px;
      `
}

const OutlinedMuiTextField = styled(MuiTextField).attrs((props) => ({
  ...props,
  variant: "outlined",
}))<{ $dark: boolean }>`
  ${(p) => getCss(p.$dark, !!p.disabled, !!p.error)}
  ${(p) => getCssForSize(p.size)}
  ${(p) => getCssForHelperText(!!p.helperText)}
`
